// src/components/reports/SupervisorReport/SupervisorChart.tsx
import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { Box, Paper, Typography } from '@mui/material';
import { SupervisorReportItem, formatCurrency, formatProfitRate } from '../../../../types/sales';

interface SupervisorChartProps {
  data: SupervisorReportItem[];
  title?: string;
  fullSize?: boolean; // 追加: フルサイズでグラフを表示するかどうか
}

export const SupervisorChart: React.FC<SupervisorChartProps> = ({ 
  data, 
  title,
  fullSize = false 
}) => {
  // Format data for chart (exclude total row)
  const chartData = data.slice(0, -1).map(item => ({
    name: item.supervisor_name,
    売上合計: Number(item.total_sales),
    // 合計粗利: Number(item.gross_profit),
    // 利益率: Number(item.gross_profit_rate),
    合計粗利: Number(item.final_profit),
    利益率: Number(item.final_profit_rate),
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry: any, index: number) => (
            <Typography 
              key={`tooltip-${index}`} 
              variant="body2" 
              sx={{ 
                color: entry.color,
                display: 'flex',
                justifyContent: 'space-between',
                mb: 0.5
              }}
            >
              <span style={{ marginRight: '12px' }}>{entry.name}:</span>
              <span style={{ fontWeight: 'bold' }}>
                {entry.dataKey === '利益率'
                  ? `${entry.value.toFixed(2)}%`
                  : formatCurrency(entry.value)
                }
              </span>
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  // サイズの設定 - fullSizeフラグに基づいて高さを変更
  const height = fullSize ? 500 : 400;

  return (
    <Box sx={{ width: '100%', height: 'auto', mt: 3 }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }} className="chart-title">
          {title}
        </Typography>
      )}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 2, 
          height: 'auto',
          minHeight: height,
          bgcolor: 'background.paper',
          textAlign: 'center'
        }}
        className="chart-container"
      >
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
              data={chartData}
              margin={{
                top: 15,
                right: 50,
                left: 40,
                bottom: 70,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={10}
                tickMargin={20}
                tick={{ fontSize: 14 }}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                tickFormatter={(value) => `¥${(value / 10000).toFixed(0)}万`}
                tick={{ fontSize: 14 }}
                label={{
                  value: '金額（円）',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -30,
                  style: { textAnchor: 'middle', fontSize: 14 }
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                tick={{ fontSize: 14 }}
                label={{
                  value: '利益率 (%)',
                  angle: 90,
                  position: 'insideRight',
                  offset: -25,
                  style: { textAnchor: 'middle', fontSize: 14 }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ bottom: -15, fontSize: 14 }}
              />
              <Bar
                yAxisId="left"
                dataKey="売上合計"
                name="売上合計"
                barSize={15}
                fill="#81C784"
              />
              <Bar
                yAxisId="left"
                dataKey="合計粗利"
                name="合計粗利"
                barSize={15}
                fill="#64B5F6"
              />
              <Line
                yAxisId="right"
                type="linear"
                dataKey="利益率"
                name="利益率 (%)"
                stroke="#FF8A65"
                strokeWidth={2}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Box>
  );
};